/**
 * T-046 retrofit: editable QR generator defaults (size).
 *
 * Reads via GET /admin/pocket-settings/qr-defaults; superadmin-edit via
 * PUT. Existing QR images are not re-rendered — the setting applies to
 * future regenerates / auto-creations only.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import { getQrDefaults, updateQrDefaults } from '@/lib/pocketSettingsApi';
import type { PocketQrDefaults } from '@/types/pocketSettings';

export function QrDefaultsCard() {
  const { isSuperadmin } = useRBAC();
  const [defaults, setDefaults] = useState<PocketQrDefaults | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftSize, setDraftSize] = useState('');
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    getQrDefaults()
      .then((data) => {
        if (!cancelled) setDefaults(data);
      })
      .catch(() => {
        // Non-fatal; the card just won't render numeric content.
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function startEdit() {
    if (!defaults) return;
    setDraftSize(String(defaults.default_size_px));
    setEditing(true);
  }

  async function saveEdit() {
    if (!defaults) return;
    const size = Number(draftSize);
    if (
      !Number.isInteger(size) ||
      size < defaults.bounds.min ||
      size > defaults.bounds.max
    ) {
      toast.error(
        `Size must be an integer between ${defaults.bounds.min} and ${defaults.bounds.max}.`,
      );
      return;
    }
    setSaving(true);
    try {
      await updateQrDefaults({ default_size_px: size });
      toast.success('QR default size saved.');
      setEditing(false);
      refresh();
    } catch {
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">QR generator defaults</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Pixel size for newly-rendered QR PNGs. Bigger = sharper print, larger
          file. 512 is right for sticker labels; bump up only for sign-boards.
        </p>
        {!defaults ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-muted-foreground">Default size</span>
              {editing ? (
                <Input
                  type="number"
                  min={defaults.bounds.min}
                  max={defaults.bounds.max}
                  value={draftSize}
                  onChange={(e) => setDraftSize(e.target.value)}
                  className="w-32"
                  aria-label="default_size_px"
                />
              ) : (
                <span className="text-2xl font-semibold tabular-nums">
                  {defaults.default_size_px}px
                </span>
              )}
              {!editing && !defaults.is_default ? (
                <Badge variant="secondary" className="text-xs">
                  override
                </Badge>
              ) : null}
            </div>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </>
              ) : isSuperadmin ? (
                <Button size="sm" variant="outline" onClick={startEdit}>
                  Edit
                </Button>
              ) : null}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              <strong>After saving:</strong> the new size applies the next time
              someone regenerates a QR or a new unit is created. Existing QRs
              keep their current size — they aren't re-rendered. Bounds:{' '}
              {defaults.bounds.min}px - {defaults.bounds.max}px.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
